import React from 'react';
import { createPortal } from 'react-dom';
import { CloseIcon } from '../../dashboard/components/product_item_components/icons';
import '../../dashboard/Dashboard.css';
import './DateRangeCalendarModal.css';

const WEEKDAY_LABELS = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'];
const MONTH_LABELS = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];

function toDateKey(year, monthIndex, day) {
  const m = String(monthIndex + 1).padStart(2, '0');
  const d = String(day).padStart(2, '0');
  return `${year}-${m}-${d}`;
}

function parseDateKey(key) {
  if (!key) return null;
  const [y, m, d] = String(key).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function buildMonths(pastCount = 2, futureCount = 1) {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - pastCount, 1);
  const total = pastCount + futureCount + 1;
  const months = [];
  for (let i = 0; i < total; i += 1) {
    const year = start.getFullYear();
    const monthIndex = start.getMonth() + i;
    const cursor = new Date(year, monthIndex, 1);
    months.push({
      key: `${cursor.getFullYear()}-${cursor.getMonth()}`,
      year: cursor.getFullYear(),
      monthIndex: cursor.getMonth(),
    });
  }
  return months;
}

function getMonthCells(year, monthIndex) {
  const first = new Date(year, monthIndex, 1);
  // Monday-first: Sun=0 -> 6, Mon=1 -> 0, ...
  const startOffset = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i += 1) cells.push(null);
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push({
      day,
      key: toDateKey(year, monthIndex, day),
    });
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function normalizeRange(startKey, endKey) {
  if (!startKey) return { start: null, end: null };
  if (!endKey || endKey === startKey) return { start: startKey, end: startKey };
  return startKey <= endKey
    ? { start: startKey, end: endKey }
    : { start: endKey, end: startKey };
}

function isInRange(key, startKey, endKey) {
  if (!key || !startKey) return false;
  const { start, end } = normalizeRange(startKey, endKey || startKey);
  return key >= start && key <= end;
}

export default function DateRangeCalendarModal({
  isOpen,
  onClose,
  initialStart = null,
  initialEnd = null,
  onApply,
}) {
  const [draftStart, setDraftStart] = React.useState(initialStart);
  const [draftEnd, setDraftEnd] = React.useState(initialEnd);
  const scrollRef = React.useRef(null);
  const currentMonthRef = React.useRef(null);
  const months = React.useMemo(() => buildMonths(2, 1), []);

  React.useEffect(() => {
    if (!isOpen) return;
    setDraftStart(initialStart);
    setDraftEnd(initialEnd);
  }, [isOpen, initialStart, initialEnd]);

  React.useEffect(() => {
    if (!isOpen) return;
    const id = requestAnimationFrame(() => {
      currentMonthRef.current?.scrollIntoView({ block: 'start' });
    });
    return () => cancelAnimationFrame(id);
  }, [isOpen]);

  if (!isOpen) return null;

  const todayKey = (() => {
    const n = new Date();
    return toDateKey(n.getFullYear(), n.getMonth(), n.getDate());
  })();

  const handleDayClick = (key) => {
    if (!draftStart || (draftStart && draftEnd && draftStart !== draftEnd)) {
      setDraftStart(key);
      setDraftEnd(null);
      return;
    }
    if (draftStart && !draftEnd) {
      if (key === draftStart) {
        setDraftEnd(key);
        return;
      }
      setDraftEnd(key);
    }
  };

  const handleApply = () => {
    if (!draftStart) {
      onApply?.({ start: null, end: null });
      onClose?.();
      return;
    }
    const { start, end } = normalizeRange(draftStart, draftEnd || draftStart);
    onApply?.({ start, end });
    onClose?.();
  };

  const handleClear = () => {
    setDraftStart(null);
    setDraftEnd(null);
    onApply?.({ start: null, end: null });
    onClose?.();
  };

  const selectionLabel = (() => {
    if (!draftStart) return 'Выберите дату или период';
    const startDate = parseDateKey(draftStart);
    const endDate = parseDateKey(draftEnd || draftStart);
    if (!startDate || !endDate) return 'Выберите дату или период';
    const fmt = (d) => d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
    const { start, end } = normalizeRange(draftStart, draftEnd || draftStart);
    if (start === end) return fmt(startDate);
    const a = parseDateKey(start);
    const b = parseDateKey(end);
    return `${fmt(a)} – ${fmt(b)}`;
  })();

  const modal = (
    <div className="main-page-modalOverlay" onClick={onClose}>
      <div
        className="main-page-modalCard date-range-calendar-card"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="main-page-modalHeader">
          <div className="main-page-modalTitle">
            Дата
            <div className="main-page-modalSubtitle">{selectionLabel}</div>
          </div>
          <button
            type="button"
            className="main-page-modalClose"
            onClick={onClose}
            aria-label="Закрыть"
            title="Закрыть"
          >
            <CloseIcon />
          </button>
        </div>

        <div className="date-range-calendar-weekdays" aria-hidden="true">
          {WEEKDAY_LABELS.map((label) => (
            <span key={label}>{label}</span>
          ))}
        </div>

        <div ref={scrollRef} className="date-range-calendar-scroll">
          {months.map(({ key, year, monthIndex }) => {
            const cells = getMonthCells(year, monthIndex);
            const isCurrentMonth =
              year === new Date().getFullYear() && monthIndex === new Date().getMonth();
            return (
              <section
                key={key}
                className="date-range-calendar-month"
                ref={isCurrentMonth ? currentMonthRef : null}
              >
                <h3 className="date-range-calendar-monthTitle">
                  {MONTH_LABELS[monthIndex]} {year}
                </h3>
                <div className="date-range-calendar-grid">
                  {cells.map((cell, idx) => {
                    if (!cell) {
                      return <span key={`e-${key}-${idx}`} className="date-range-calendar-day is-empty" />;
                    }
                    const inRange = isInRange(cell.key, draftStart, draftEnd);
                    const { start, end } = normalizeRange(draftStart, draftEnd || draftStart);
                    const isStart = cell.key === start;
                    const isEnd = cell.key === end;
                    const isToday = cell.key === todayKey;
                    return (
                      <button
                        key={cell.key}
                        type="button"
                        className={[
                          'date-range-calendar-day',
                          inRange ? 'is-in-range' : '',
                          isStart || isEnd ? 'is-selected' : '',
                          isStart && start !== end ? 'is-range-start' : '',
                          isEnd && start !== end ? 'is-range-end' : '',
                          isToday ? 'is-today' : '',
                        ].filter(Boolean).join(' ')}
                        onClick={() => handleDayClick(cell.key)}
                      >
                        {cell.day}
                      </button>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>

        <div className="date-range-calendar-actions">
          <button
            type="button"
            className="main-page-editButton main-page-editButtonSecondary"
            onClick={handleClear}
          >
            Сбросить
          </button>
          <button
            type="button"
            className="main-page-editButton main-page-editButtonPrimary"
            onClick={handleApply}
          >
            Применить
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return modal;
  return createPortal(modal, document.body);
}

export function formatDateFilterLabel(start, end) {
  if (!start) return 'даты';
  const a = parseDateKey(start);
  const b = parseDateKey(end || start);
  if (!a || !b) return 'даты';
  const fmt = (d) => d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
  if (start === (end || start)) return fmt(a);
  return `${fmt(a)} – ${fmt(b)}`;
}
